import API_CONFIG from '../config/api';

export interface Insurance {
  _id: string;
  name: string;
  active: boolean;
  requiresExtraCode: boolean;
  requiresInsuranceBooklet: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateInsuranceData {
  name: string;
  active?: boolean;
  requiresExtraCode?: boolean;
  requiresInsuranceBooklet?: boolean;
}

export interface UpdateInsuranceData {
  name?: string;
  active?: boolean;
  requiresExtraCode?: boolean;
  requiresInsuranceBooklet?: boolean;
}

// Function to get auth headers with token
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    },
  };
};

/**
 * Get active insurances (for public use)
 */
export const getActiveInsurances = async (): Promise<Insurance[]> => {
  try {
    const response = await fetch(API_CONFIG.getUrl('insurances/active'), {
      method: 'GET',
      ...getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || 'خطا در دریافت بیمه های فعال');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Get active insurance by ID (for public use)
 */
export const getActiveInsuranceById = async (id: string): Promise<Insurance> => {
  try {
    const response = await fetch(API_CONFIG.getUrl(`insurances/details/${id}`), {
      method: 'GET',
      ...getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || 'خطا در دریافت بیمه');
    }
  } catch (error) {

    throw error;
  }
};

export interface InsurancePaginationResponse {
  data: Insurance[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    limit: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

/**
 * Get all insurances (admin only) with pagination
 */
export const getAllInsurances = async (page: number = 1, limit: number = 6): Promise<InsurancePaginationResponse> => {
  try {
    const response = await fetch(API_CONFIG.getUrl(`insurances?page=${page}&limit=${limit}`), {
      method: 'GET',
      ...getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return {
        data: result.data,
        pagination: result.pagination
      };
    } else {
      throw new Error(result.message || 'خطا در دریافت بیمه ها');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Get insurance by ID (admin only)
 */
export const getInsuranceById = async (id: string): Promise<Insurance> => {
  try {
    const response = await fetch(API_CONFIG.getUrl(`insurances/${id}`), {
      method: 'GET',
      ...getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || 'خطا در دریافت بیمه');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Create a new insurance (admin only)
 */
export const createInsurance = async (data: CreateInsuranceData): Promise<Insurance> => {
  try {
    const response = await fetch(API_CONFIG.getUrl('insurances'), {
      method: 'POST',
      ...getAuthHeaders(),
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || 'خطا در ایجاد بیمه');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Update an insurance (admin only)
 */
export const updateInsurance = async (id: string, data: UpdateInsuranceData): Promise<Insurance> => {
  try {
    const response = await fetch(API_CONFIG.getUrl(`insurances/${id}`), {
      method: 'PUT',
      ...getAuthHeaders(),
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || 'خطا در به‌روزرسانی بیمه');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Toggle insurance status (admin only)
 */
export const toggleInsuranceStatus = async (id: string): Promise<Insurance> => {
  try {
    const response = await fetch(API_CONFIG.getUrl(`insurances/${id}/toggle-status`), {
      method: 'PUT',
      ...getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || 'خطا در تغییر وضعیت بیمه');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Delete an insurance (admin only)
 */
export const deleteInsurance = async (id: string): Promise<void> => {
  try {
    const response = await fetch(API_CONFIG.getUrl(`insurances/${id}`), {
      method: 'DELETE',
      ...getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.message || 'خطا در حذف بیمه');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Utility function to get insurance name by ID with caching
 * This can be used across components to resolve insurance names
 */
const insuranceCache = new Map<string, Insurance>();

export const getInsuranceNameById = async (insuranceId: string): Promise<string> => {
  if (!insuranceId) return '';
  
  // Check cache first
  if (insuranceCache.has(insuranceId)) {
    const cached = insuranceCache.get(insuranceId);
    return cached ? cached.name : 'حذف شده';
  }
  
  try {
    const insurance = await getActiveInsuranceById(insuranceId);
    insuranceCache.set(insuranceId, insurance);
    return insurance.name;
  } catch (error) {

    // If insurance not found or deleted, return "حذف شده"
    return 'حذف شده';
  }
};

/**
 * Clear insurance cache (useful when insurance data changes)
 */
export const clearInsuranceCache = () => {
  insuranceCache.clear();
}; 